import { z } from 'zod';

// ---------------------------------------------------------------------------
// Policy acceptance: staff sign off on published corpus items (policies) and
// sign again when a material change is published or a periodic
// reconfirmation falls due. Tables policy_releases / policy_acceptances
// (migration 0019); routes in apps/worker/src/routes/policy-acceptance.ts.
// User-facing verbs: publish for acceptance, accept, outstanding, overdue.
// ---------------------------------------------------------------------------

/**
 * The exact wording a person agrees to. The server stores the rendered string
 * on every acceptance row; the web shows the same string beside the checkbox.
 * `version` is the document's own label ("3.1.0") when it has one, else the
 * application revision number.
 */
export function policyAcceptanceStatement(title: string, version: string | number): string {
  return `I confirm that I have read and understood the ${title} (version ${version}) and agree to comply with it.`;
}

export const POLICY_DUE_DAYS_DEFAULT = 14;

export const PolicyAcceptanceMethodSchema = z.enum(['in_app', 'recorded']);
export type PolicyAcceptanceMethod = z.infer<typeof PolicyAcceptanceMethodSchema>;

export const PolicyAcceptanceStatusValueSchema = z.enum(['accepted', 'outstanding', 'overdue']);
export type PolicyAcceptanceStatusValue = z.infer<typeof PolicyAcceptanceStatusValueSchema>;

/**
 * Why an acceptance is outstanding: `never` (no acceptance of this policy at
 * all), `new_version` (an earlier epoch was accepted), `periodic` (the last
 * acceptance lapsed under the release's reconfirmation interval).
 */
export const PolicyOutstandingReasonSchema = z.enum(['never', 'new_version', 'periodic']);
export type PolicyOutstandingReason = z.infer<typeof PolicyOutstandingReasonSchema>;

/** One member's standing against one live release. */
export const PolicyAcceptanceStatusSchema = z.object({
  corpusItemId: z.string(),
  releaseId: z.string(),
  status: PolicyAcceptanceStatusValueSchema,
  reason: PolicyOutstandingReasonSchema.nullable(),
  /** Calendar date (YYYY-MM-DD) the acceptance is due; null once accepted. */
  dueOn: z.string().nullable(),
  acceptedAt: z.string().nullable(),
  /** The version the standing acceptance was given for (may predate the live release). */
  acceptedVersion: z.number().int().nullable(),
  /** That version's own document label ('' when it named none); null when not accepted. */
  acceptedDocumentVersion: z.string().nullable(),
  method: PolicyAcceptanceMethodSchema.nullable(),
  /** When periodic reconfirmation applies: the day the current acceptance lapses. */
  renewBy: z.string().nullable(),
});
export type PolicyAcceptanceStatus = z.infer<typeof PolicyAcceptanceStatusSchema>;

export const PolicyReleaseSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  corpusItemId: z.string(),
  versionNumber: z.number().int(),
  /** The released revision's own document version label ('' when it names none). */
  documentVersion: z.string(),
  contentSha256: z.string(),
  requiresAcceptance: z.boolean(),
  acceptanceEpoch: z.number().int(),
  releaseNotes: z.string(),
  dueDays: z.number().int(),
  reacceptIntervalMonths: z.number().int().nullable(),
  publishedBy: z.string(),
  publishedByName: z.string().nullable(),
  publishedAt: z.string(),
  supersededAt: z.string().nullable(),
  withdrawn: z.boolean(),
  /** Acceptances recorded against this exact release. */
  acceptanceCount: z.number().int(),
});
export type PolicyRelease = z.infer<typeof PolicyReleaseSchema>;

export const PublishPolicyInputSchema = z.object({
  /** Defaults to the item's current version; must exist. */
  versionNumber: z.number().int().min(1).optional(),
  /** Material change: everyone must accept again. Forced on for a first release. */
  requiresAcceptance: z.boolean(),
  releaseNotes: z.string().max(2000).default(''),
  dueDays: z.number().int().min(1).max(90).default(POLICY_DUE_DAYS_DEFAULT),
  reacceptIntervalMonths: z.number().int().min(1).max(36).nullable().default(null),
  /** Email staff now (only when the release requires acceptance). */
  notify: z.boolean().default(true),
});
export type PublishPolicyInput = z.infer<typeof PublishPolicyInputSchema>;

export const RecordPolicyAcceptanceInputSchema = z.object({
  userId: z.string().min(1),
  corpusItemId: z.string().min(1),
  /** Calendar date (YYYY-MM-DD), not in the future. */
  acceptedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Provenance, e.g. "Signed paper form" or the previous platform. */
  source: z.string().min(1).max(500),
});
export type RecordPolicyAcceptanceInput = z.infer<typeof RecordPolicyAcceptanceInputSchema>;

export const AcceptPolicyInputSchema = z.object({
  confirm: z.literal(true),
});
export type AcceptPolicyInput = z.infer<typeof AcceptPolicyInputSchema>;

export const RemindPolicyInputSchema = z.object({
  corpusItemId: z.string().min(1).optional(),
});
export type RemindPolicyInput = z.infer<typeof RemindPolicyInputSchema>;

/** A live release as listed for staff or the tracker. */
export const PolicyReleaseSummarySchema = z.object({
  releaseId: z.string(),
  corpusItemId: z.string(),
  title: z.string(),
  key: z.string(),
  versionNumber: z.number().int(),
  documentVersion: z.string(),
  publishedAt: z.string(),
  releaseNotes: z.string(),
  dueDays: z.number().int(),
  reacceptIntervalMonths: z.number().int().nullable(),
});
export type PolicyReleaseSummary = z.infer<typeof PolicyReleaseSummarySchema>;

export const MyPolicySchema = PolicyReleaseSummarySchema.extend({
  status: PolicyAcceptanceStatusSchema,
});
export type MyPolicy = z.infer<typeof MyPolicySchema>;

export const MyPoliciesSchema = z.object({
  items: z.array(MyPolicySchema),
  counts: z.object({
    accepted: z.number().int(),
    outstanding: z.number().int(),
    overdue: z.number().int(),
  }),
});
export type MyPolicies = z.infer<typeof MyPoliciesSchema>;

export const PolicyAcceptanceRecordSchema = z.object({
  id: z.string(),
  releaseId: z.string(),
  versionNumber: z.number().int(),
  documentVersion: z.string(),
  method: PolicyAcceptanceMethodSchema,
  acceptedAt: z.string(),
  recordedBy: z.string().nullable(),
  recordedByName: z.string().nullable(),
  source: z.string(),
});
export type PolicyAcceptanceRecord = z.infer<typeof PolicyAcceptanceRecordSchema>;

/** The reader: the released text, the statement and where the caller stands. */
export const MyPolicyDetailSchema = z.object({
  release: PolicyReleaseSummarySchema.extend({
    /** False once superseded or withdrawn; `currentReleaseId` then points at the live one. */
    live: z.boolean(),
    currentReleaseId: z.string().nullable(),
  }),
  contentMarkdown: z.string(),
  statement: z.string(),
  /** Null when the release is not live (nothing to accept). */
  status: PolicyAcceptanceStatusSchema.nullable(),
  history: z.array(PolicyAcceptanceRecordSchema),
});
export type MyPolicyDetail = z.infer<typeof MyPolicyDetailSchema>;

export const PolicyAcceptanceMemberSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  joinedAt: z.string(),
  policies: z.array(PolicyAcceptanceStatusSchema),
});
export type PolicyAcceptanceMember = z.infer<typeof PolicyAcceptanceMemberSchema>;

export const PolicyAcceptanceMatrixSchema = z.object({
  policies: z.array(PolicyReleaseSummarySchema),
  members: z.array(PolicyAcceptanceMemberSchema),
  counts: z.object({
    accepted: z.number().int(),
    outstanding: z.number().int(),
    overdue: z.number().int(),
    total: z.number().int(),
  }),
});
export type PolicyAcceptanceMatrix = z.infer<typeof PolicyAcceptanceMatrixSchema>;

export const PolicyRemindResultSchema = z.object({
  recipients: z.number().int(),
});
export type PolicyRemindResult = z.infer<typeof PolicyRemindResultSchema>;
